import Constant from "../Constant";

export interface RouteRestriction {
  routePath: string;
  allowedRoles: string[];
  allowImpersonation?: boolean; // defaults to true — set false to block impersonated sessions
  fallbackPath?: string | ((userRole: string) => string);
  accessDeniedMessage?: string;
}

export const ROUTE_RESTRICTIONS: RouteRestriction[] = [
  {
    routePath: "/master/edit-barrel/:id",
    allowedRoles: [Constant.Role.SUPER_ADMIN],
    accessDeniedMessage: "You don't have permission to edit container details"
  },
  {
    routePath: "/master/edit-lot-details/:id",
    allowedRoles: [Constant.Role.SUPER_ADMIN],
    accessDeniedMessage: "You don't have permission to edit lot details"
  },
  {
    routePath: "/user/edit-barrel/:id",
    allowedRoles: [Constant.Role.SUPER_ADMIN],
    accessDeniedMessage: "You don't have permission to edit container details"
  },
  {
    routePath: "/user/edit-lot-details/:id",
    allowedRoles: [Constant.Role.SUPER_ADMIN],
    accessDeniedMessage: "You don't have permission to edit lot details"
  },
  {
    routePath: "/user/container-detail/:id",
    allowedRoles: [Constant.Role.SUPER_ADMIN],
    accessDeniedMessage: "You don't have permission to view container details"
  },
  {
    routePath: "/user/container-history-detail/:id",
    allowedRoles: [Constant.Role.SUPER_ADMIN],
    accessDeniedMessage: "You don't have permission to view container history details"
  },
  {
    routePath: "/user/valuation-history/:id",
    allowedRoles: [Constant.Role.SUPER_ADMIN],
    accessDeniedMessage: "You don't have permission to view valuation details"
  },
  {
    routePath: "/master/valuation-history/:id",
    allowedRoles: [Constant.Role.SUPER_ADMIN],
    accessDeniedMessage: "You don't have permission to view valuation details"
  },
  {
    routePath: "/user/proof-history/:id",
    allowedRoles: [Constant.Role.SUPER_ADMIN],
    accessDeniedMessage: "You don't have permission to view proof details"
  },
  {
    routePath: "/master/proof-history/:id",
    allowedRoles: [Constant.Role.SUPER_ADMIN],
    accessDeniedMessage: "You don't have permission to view proof details"
  },
  {
    routePath: "master/dashboard",
    allowedRoles: [Constant.Role.SUPER_ADMIN],
    accessDeniedMessage: "You don't have permission to view dashboard"
  },
  {
    routePath: "/master/container-detail/:id",
    allowedRoles: [Constant.Role.SUPER_ADMIN],
    accessDeniedMessage: "You don't have permission to view container details"
  }
];

export class RouteAccessControl {
  private static matchRoute(routePath: string, configPath: string): boolean {
    const routeParts = routePath.split('/').filter(Boolean);
    const configParts = configPath.split('/').filter(Boolean);
    
    if (routeParts.length !== configParts.length) {
      return false;
    }
    
    return configParts.every((part, index) => {
      if (part.startsWith(':')) {
        return true;
      }
      return part === routeParts[index];
    });
  }

  static isRouteAllowed(routePath: string, userRole: string, isImpersonating: boolean = false): boolean {
    const restriction = ROUTE_RESTRICTIONS.find(r => 
      this.matchRoute(routePath, r.routePath)
    );
    
    if (!restriction) {
      return true;
    }

    // Impersonation is allowed by default (allowImpersonation defaults to true)
    if (isImpersonating && restriction.allowImpersonation !== false) {
      return true;
    }
    
    return restriction.allowedRoles.includes(userRole);
  }

  static getRouteRestriction(routePath: string): RouteRestriction | undefined {
    return ROUTE_RESTRICTIONS.find(r => 
      this.matchRoute(routePath, r.routePath)
    );
  }

  static getFallbackPath(routePath: string, userRole: string): string | null {
    const restriction = this.getRouteRestriction(routePath);
    
    if (!restriction || this.isRouteAllowed(routePath, userRole)) {
      return null;
    }
    
    if (restriction.fallbackPath) {
      if (typeof restriction.fallbackPath === 'function') {
        return restriction.fallbackPath(userRole);
      }
      return restriction.fallbackPath;
    }
    
    return this.getDefaultFallback(userRole);
  }

  static getAccessDeniedMessage(routePath: string): string {
    const restriction = this.getRouteRestriction(routePath);
    return restriction?.accessDeniedMessage || "You don't have permission to access this page";
  }

  private static getDefaultFallback(userRole: string): string {
    if (userRole === Constant.Role.SUPER_ADMIN) {
      return "/master/dashboard";
    }
    if (userRole === Constant.Role.WAREHOUSE) {
      return "/master/storage-dashboard";
    }
    return "/user/inventories";
  }
}
